import { createAsyncThunk } from "@reduxjs/toolkit";
import { auth, db } from "@/lib/firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut,
  updateProfile,
  sendEmailVerification,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  sendPasswordResetEmail,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  where,
  query,
  Timestamp,
  onSnapshot,
} from "firebase/firestore";
import { toast } from "../../components/ui/use-toast";
import axiosInstance from "../../utils/axiosInstance";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { getTotalCount } from "./authSlice";
import axios from "axios";
import { clearMessages } from "../aiAssistant/aiAssitantSlice";
const storage = getStorage();

const actionCodeSettings = {
  url: `${import.meta.env.VITE_APP_BASE_URL}?authRedirect=true`,
  handleCodeInApp: true,
};

const baseURL = import.meta.env.VITE_API_BASE_URL;

const fetchUserData = async (firebaseUser) => {
  if (!firebaseUser) return null;

  const userDocRef = doc(db, "users", firebaseUser.uid);
  const userDoc = await getDoc(userDocRef);

  if (userDoc.exists()) {
    const userData = {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      isVerified: firebaseUser.emailVerified,
      ...userDoc.data(),
    };
    return userData;
  } else {
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      isVerified: firebaseUser.emailVerified,
    };
  }
};

export const checkAuthState = createAsyncThunk(
  "auth/checkAuthState",
  async (_, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(
        auth,
        async (firebaseUser) => {
          unsubscribe();
          try {
            const userData = await fetchUserData(firebaseUser);
            resolve({
              user: userData,
              token: firebaseUser ? await firebaseUser.getIdToken() : null,
            });
          } catch (error) {
            reject(
              rejectWithValue(error?.message || "Error checking auth state"),
            );
          }
        },
        (error) => {
          reject(rejectWithValue(error?.message || "Auth observer error"));
        },
      );
    });
  },
);

export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);

      if (!user.emailVerified) {
        throw new Error(
          "Please verify your email before logging in. Check your inbox.",
        );
      }

      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error("No profile was found for this user.");
      }

      const userDetail = userDoc.data();
      if (userDetail?.role !== "seller" && userDetail?.role !== "buyer") {
        throw new Error(
          "Access denied. Only users with a Seller or Buyer account can log in.",
        );
      }
      await setDoc(
        userDocRef,
        {
          isVerified: user.emailVerified,
        },
        { merge: true },
      );

      const loggedInUser = {
        uid: user.uid,
        isVerified: user?.emailVerified,
        email: user.email,
        ...userDoc.data(),
      };

      toast({
        title: "Login Successful!",
        description: "Welcome back!",
        variant: "default",
      });

      return {
        user: loggedInUser,
        token: await user?.getIdToken(),
      };
    } catch (error) {
      console.error("Firebase login error:", error);
      let message = "Something went wrong while logging in.";
      if (error.code) {
        switch (error.code) {
          case "auth/user-not-found":
            message = "No account found with this email.";
            break;
          case "auth/wrong-password":
            message = "Incorrect password. Please try again.";
            break;
          case "auth/invalid-email":
            message = "Invalid email address. Please check and try again.";
            break;
          case "auth/invalid-credential":
            message =
              "The email or password you entered is incorrect. Please try again.";
            break;
          case "auth/too-many-requests":
            message =
              "Too many failed attempts. Please try again later or reset your password.";
            break;
          default:
            message = error.message || message;
        }
      } else if (error.message) {
        message = error.message;
      }

      toast({
        title: "Login Failed",
        description: message,
        variant: "destructive",
      });

      return rejectWithValue(message);
    }
  },
);

export const signInWithGoogle = createAsyncThunk(
  "auth/signInWithGoogle",
  async (_, { rejectWithValue }) => {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope("email");
      provider.addScope("profile");

      const result = await signInWithPopup(auth, provider);
      const firebaseUser = result.user;

      if (!firebaseUser || !firebaseUser.uid || !firebaseUser.email) {
        throw new Error("Incomplete user data received from Google");
      }

      const userDocRef = doc(db, "users", firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);
      const userDetail = userDoc?.data();
      if (
        userDoc.exists() &&
        (userDetail?.role == "seller" || userDetail?.role == "buyer")
      ) {
        const existingUserData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          ...userDoc.data(),
        };

        toast({
          title: "Sign-in successful!",
          description: "Welcome back with Google!",
          variant: "default",
        });

        return {
          user: existingUserData,
          token: await firebaseUser.getIdToken(),
          isExistingUser: true,
        };
      } else {
        const userData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          name: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
        };

        const token = await firebaseUser.getIdToken();

        return {
          user: userData,
          token: token,
          isExistingUser: false,
          requiresRoleSelection: true,
        };
      }
    } catch (error) {
      console.error("Google sign-in error:", error);

      let message = error?.message || "Error al iniciar sesión con Google";

      if (error.code === "auth/popup-closed-by-user") {
        return rejectWithValue("SIGN_IN_CANCELLED");
      } else if (error.code === "auth/popup-blocked") {
        message = "Pop-up blocked. Please allow pop-ups and try again.";
      } else if (error.code === "auth/network-request-failed") {
        message = "Network error. Please check your connection and try again.";
      } else if (error.code === "auth/internal-error") {
        message = "An internal error occurred. Please try again.";
      } else if (error.code === "auth/cancelled-popup-request") {
        message = "Sign-in was cancelled. Please try again.";
      }

      // Only show toast for actual errors (not cancellations)
      if (error.code !== "auth/popup-closed-by-user") {
        toast({
          title: "Error con Google Sign-In",
          description: message,
          variant: "destructive",
        });
      }

      return rejectWithValue(message);
    }
  },
);

export const completeGoogleSignUp = createAsyncThunk(
  "auth/completeGoogleSignUp",
  async (
    { role, companyName, postalCode, callback },
    { getState, rejectWithValue },
  ) => {
    try {
      const { auth: authState } = getState();
      const partialUser = authState.googleSignInData?.user;

      if (!partialUser || !partialUser.uid) {
        throw new Error("No user data found. Please try signing in again.");
      }

      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error(
          "No authenticated Firebase user found. Please try signing in again.",
        );
      }

      const userProfileData = {
        role: role,
        name: partialUser.name || currentUser.displayName || null,
        email: partialUser.email || currentUser.email,
        photoURL: partialUser.photoURL || currentUser.photoURL || null,
        companyName: role === "seller" ? companyName || null : null,
        postalCode: role === "seller" ? postalCode || null : null,
        createdAt: new Date().toISOString(),
        isFirstLogin: true,
        membership: {
          plan: null,
          status: "inactive",
          startDate: null,
        },
        authProvider: "google",
      };

      // Save user profile to Firestore
      await setDoc(doc(db, "users", currentUser.uid), userProfileData, {
        merge: true,
      });

      // Update Firebase user profile if displayName is not set
      if (!currentUser.displayName && partialUser.name) {
        await updateProfile(currentUser, {
          displayName: partialUser.name,
        });
      }

      // Create complete user data object
      const completeUserData = {
        uid: currentUser.uid,
        email: currentUser.email,
        ...userProfileData,
      };

      // Get the Firebase ID token from the current user
      const token = await currentUser.getIdToken();

      toast({
        title: "¡Registro completado!",
        description: "Tu cuenta de Google ha sido configurada exitosamente.",
        variant: "default",
        duration: 7000,
      });

      // Execute callback if provided
      if (typeof callback === "function") {
        callback(role);
      }

      return {
        user: completeUserData,
        token: token,
        isCompleted: true,
      };
    } catch (error) {
      const message =
        error?.message || "Error completando el registro con Google";
      console.error("Complete Google signup error:", error);

      toast({
        title: "Error completando registro",
        description: message,
        variant: "destructive",
      });

      return rejectWithValue(message);
    }
  },
);

const getFriendlyFirebaseError = (error) => {
  const code = error?.code;

  switch (code) {
    case "auth/email-already-in-use":
      return "This email address is already registered. Please try logging in instead.";
    case "auth/invalid-email":
      return "The email address entered is not valid. Please check and try again.";
    case "auth/operation-not-allowed":
      return "Email and password sign-up is currently disabled. Please contact support.";
    case "auth/weak-password":
      return "Your password is too weak. Please use at least 6 characters including letters and numbers.";
    case "auth/missing-email":
      return "Please enter your email address.";
    case "auth/missing-password":
      return "Please enter your password.";
    case "auth/network-request-failed":
      return "Network error occurred. Please check your internet connection and try again.";
    case "auth/internal-error":
      return "An unexpected error occurred. Please try again later.";
    default:
      return "Something went wrong while creating your account. Please try again.";
  }
};

export const signup = createAsyncThunk(
  "auth/signup",
  async ({ signupData }, { rejectWithValue }) => {
    try {
      const { email, password, role, name, companyName, postalCode } =
        signupData;
      const { user: firebaseUser } = await createUserWithEmailAndPassword(
        auth,
        email,
        password,
      );

      const userProfileData = {
        role: role,
        name: name || null,
        email,
        companyName: companyName || null,
        postalCode: postalCode || null,
        createdAt: new Date().toISOString(),
        isFirstLogin: true,
        membership: {
          plan: null,
          status: "inactive",
          startDate: null,
        },
        authProvider: "email",
      };

      await updateProfile(firebaseUser, {
        displayName: name || companyName || "",
      });

      await setDoc(doc(db, "users", firebaseUser.uid), userProfileData, {
        merge: true,
      });

      // await sendEmailVerification(firebaseUser, actionCodeSettings);
      await axios.post(`${baseURL}/auth/send-verification`, {
        email: firebaseUser?.email,
        name: firebaseUser?.displayName,
      });

      // Sign out the user after registration
      await signOut(auth);

      let welcomeMessage = `Welcome! Your account has been created. Please check your email to verify your account before logging in.`;

      toast({
        title: "Registration successful!",
        description: welcomeMessage,
        variant: "default",
        duration: 7000,
      });

      return {
        user: userProfileData,
        token: null,
      };
    } catch (error) {
      console.error("Firebase signup error:", error);
      const message = getFriendlyFirebaseError(error);
      toast({
        title: "Registration Failed",
        description: message,
        variant: "destructive",
        duration: 6000,
      });
      return rejectWithValue(message);
    }
  },
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      await signOut(auth);
      dispatch(clearMessages());
      toast({
        title: "Logged Out",
        description: "You have successfully logged out.",
      });
      return null;
    } catch (error) {
      const message = error?.message || "Error logging out";
      console.error("Firebase logout error:", error);
      toast({
        title: "Logout Failed",
        description: message,
        variant: "destructive",
      });
      return rejectWithValue(message);
    }
  },
);

export const updateUserProfile = createAsyncThunk(
  "auth/updateUserProfile",
  async (updatedData, { getState, rejectWithValue }) => {
    try {
      const { auth: authState } = getState();
      const currentUser = authState.user;

      if (!currentUser || !auth.currentUser) {
        throw new Error("User not authenticated");
      }

      const userId = currentUser.uid || auth.currentUser.uid;
      let processedData = { ...updatedData };

      if (
        processedData.logoUrl &&
        processedData.logoUrl.startsWith("data:image/")
      ) {
        try {
          const storageRef = ref(storage, `users/${userId}/logo`);
          const response = await fetch(processedData.logoUrl);
          const blob = await response.blob();
          const snapshot = await uploadBytes(storageRef, blob);
          const logoUrl = await getDownloadURL(snapshot.ref);
          processedData.logoUrl = logoUrl;
        } catch (uploadError) {
          console.error("Logo upload error:", uploadError);
          delete processedData.logoUrl;
          toast({
            title: "Logo Upload Failed",
            description:
              "Profile updated but logo upload failed. Please try again.",
            variant: "destructive",
          });
        }
      }

      const userDocRef = doc(db, "users", userId);
      await setDoc(userDocRef, processedData, { merge: true });

      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
        variant: "default",
      });
      return { ...currentUser, ...processedData };
    } catch (error) {
      const message =
        error?.message || "Failed to update profile. Please try again.";
      console.error("Update user profile error:", error);

      if (!message.includes("User not authenticated")) {
        toast({
          title: "Update Error",
          description: message,
          variant: "destructive",
        });
      }

      return rejectWithValue(message);
    }
  },
);

export const addSellerLead = createAsyncThunk(
  "seller/addSellerLead",
  async (leadData, { rejectWithValue }) => {
    try {
      const docRef = await addDoc(collection(db, "sellerLeads"), {
        ...leadData,
        submittedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      });
      return { id: docRef.id, ...leadData };
    } catch (error) {
      return rejectWithValue(error.message || "Failed to save seller lead");
    }
  },
);

// 🔹 Refresh User
export const refreshUser = createAsyncThunk(
  "auth/refreshUser",
  async (_, { getState, rejectWithValue }) => {
    try {
      const {
        auth: { user, isAuthenticated },
      } = getState();

      if (!isAuthenticated || !user?.uid) {
        throw new Error("No authenticated user found");
      }

      const firebaseUser = auth.currentUser;
      if (!firebaseUser) {
        throw new Error("No authenticated Firebase user found");
      }

      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        throw new Error("User document not found");
      }

      return {
        uid: user.uid,
        email: user.email,
        ...userDoc.data(),
      };
    } catch (error) {
      console.error("Failed to refresh user:", error);
      toast({
        title: "Error",
        description:
          error?.message || "No se pudo actualizar los datos del usuario.",
        variant: "destructive",
      });
      return rejectWithValue(error?.message || "Error fetching user data");
    }
  },
);

export const updateUserPassword = createAsyncThunk(
  "auth/updateUserPassword",
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const currentUser = auth.currentUser;

      if (!currentUser || !currentUser.email) {
        throw new Error("No authenticated user found");
      }

      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword,
      );
      await reauthenticateWithCredential(currentUser, credential);

      await updatePassword(currentUser, newPassword);

      toast({
        title: "Password Updated",
        description: "Your password has been successfully updated.",
        variant: "default",
      });

      return { success: true };
    } catch (error) {
      console.error("Password update error:", error);

      let errorMessage = "Failed to update password. Please try again.";

      if (error.code === "auth/wrong-password") {
        errorMessage = "Current password is incorrect.";
      } else if (error.code === "auth/weak-password") {
        errorMessage =
          "New password is too weak. Please choose a stronger password.";
      } else if (error.code === "auth/requires-recent-login") {
        errorMessage =
          "Please log out and log back in before changing your password.";
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = "Too many attempts. Please try again later.";
      }

      toast({
        title: "Password Update Failed",
        description: errorMessage,
        variant: "destructive",
      });

      return rejectWithValue(errorMessage);
    }
  },
);

// Get User Subscription
export const getUserSubscription = createAsyncThunk(
  "parts/getUserSubscription",
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) {
        throw new Error("User ID is required to fetch subscription");
      }

      const response = await axiosInstance.get(`/subscriptions/user/${userId}`);
      const { data } = response.data;

      return data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "There was an error fetching the user subscription.";

      return rejectWithValue(message);
    }
  },
);

export const validateCurrentPassword = createAsyncThunk(
  "auth/validateCurrentPassword",
  async ({ email, currentPassword }, { rejectWithValue }) => {
    try {
      await signInWithEmailAndPassword(auth, email, currentPassword);
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        currentPassword,
      );
      return { success: true };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const cancelGoogleSignUp = createAsyncThunk(
  "auth/cancelGoogleSignUp",
  async (_, { rejectWithValue }) => {
    try {
      await signOut(auth);
      return { cancelled: true };
    } catch (error) {
      console.error("Error during Google sign-up cancellation:", error);
      return rejectWithValue("Failed to cancel sign-up");
    }
  },
);

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const sendPasswordReset = createAsyncThunk(
  "auth/sendPasswordReset",
  async (email, { rejectWithValue }) => {
    try {
      const cleanEmail = email.trim().toLowerCase();

      if (!cleanEmail) {
        throw new Error("Email is required.");
      }

      if (!emailRegex.test(cleanEmail)) {
        throw new Error("Invalid email format.");
      }

      // Check if email exists in users collection
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", cleanEmail));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error("No account found with this email.");
      }

      let userData = null;
      querySnapshot.forEach((docSnap) => {
        userData = docSnap.data();
      });
      // await sendPasswordResetEmail(auth, cleanEmail, actionCodeSettings);
      await axios.post(`${baseURL}/auth/request-password-reset`, {
        email: email,
        name: userData?.name,
      });

      toast({
        title: "Reset Email Sent",
        description:
          "Please check your inbox and follow the link to reset your password.",
        variant: "default",
        duration: 6000,
      });

      return { success: true };
    } catch (error) {
      console.error("Password reset error:", error);

      let message =
        error?.message || "Failed to send reset password email. Try again.";

      if (error.code === "auth/user-not-found") {
        message = "No account found with this email.";
      } else if (error.code === "auth/invalid-email") {
        message = "Invalid email format.";
      } else if (error.code === "auth/invalid-credential") {
        message = "Password not matches with current password";
      }

      toast({
        title: "Password Reset Failed",
        description: message,
        variant: "destructive",
      });

      return rejectWithValue(message);
    }
  },
);

// Get User Default Payment Method
export const getUserDefaultPaymentMethod = createAsyncThunk(
  "parts/getUserDefaultPaymentMethod",
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) {
        throw new Error("User ID is required to fetch default payment method");
      }

      const response = await axiosInstance.get(
        `/paymentMethod/user/${userId}/default`,
      );
      const { data } = response.data;

      return data;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "There was an error fetching the default payment method.";

      console.error("Get user default payment method error:", error);

      // toast({
      //   title: "Failed to Load Payment Method",
      //   description: message,
      //   variant: "destructive"
      // });

      return rejectWithValue(message);
    }
  },
);

export const getUserPartsCount =
  ({ userId, startDate }) =>
  (dispatch) => {
    if (!userId) return;

    const partsRef = collection(db, "parts");

    // If no startDate is provided (e.g. free-plan users), count all parts for this seller.
    if (!startDate) {
      const q = query(partsRef, where("sellerId", "==", userId));
      onSnapshot(q, (snapshot) => {
        dispatch(getTotalCount(snapshot.size));
      });
      return;
    }

    const start =
      startDate instanceof Timestamp
        ? startDate
        : Timestamp.fromDate(new Date(startDate));

    const q = query(
      partsRef,
      where("sellerId", "==", userId),
      where("dateAdded", ">=", start),
    );

    onSnapshot(q, (snapshot) => {
      dispatch(getTotalCount(snapshot.size));
    });
  };

export const checkCompanyNameExists = createAsyncThunk(
  "auth/checkCompanyNameExists",
  async (companyName, { rejectWithValue }) => {
    try {
      if (!companyName?.trim()) {
        return rejectWithValue("Company name cannot be empty");
      }

      const q = query(
        collection(db, "users"),
        where("companyName", "==", companyName.trim()),
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        return { exists: true };
      }

      return { exists: false };
    } catch (error) {
      console.error("Error checking company name:", error);
      return rejectWithValue(error.message);
    }
  },
);
